import { motion } from 'framer-motion'
import { useState, type ReactNode } from 'react'
import CTA from '../CTA'
import ExpertiseHero from './ExpertiseHero'
import ScopeTable from './ScopeTable'
import type { ExpertiseContent } from './useExpertiseContent'

type Props = {
  content: ExpertiseContent
  /** Libellé du fil d'Ariane courant (ex. « ADR »). */
  breadcrumbLabel: string
  /** Visuel signature hero (A3 losange / B3 bouclier). */
  heroVisual?: ReactNode
  /** Visuel signature de la section périmètre (A1 tri / B2 jauge). */
  scopeVisual?: ReactNode
  /** Visuel de la section « comment ça marche » (A2 flux scindé / B1 FEFO). */
  processVisual?: ReactNode
}

// ── Section heading partagé (eyebrow pill + h2) ──
function SectionHead({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <>
      <motion.span
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
        </span>
        <span className="font-medium">{eyebrow}</span>
      </motion.span>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="mt-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(1.8rem,4vw,2.8rem)] leading-[1.1] max-w-3xl"
      >
        {title}
      </motion.h2>
    </>
  )
}

/**
 * Layout partagé des pages Expertise (ADR / AFSCA). Structure verrouillée par
 * le spec §2 : Hero → Hook → Périmètre → Comment ça marche → Conformité →
 * FAQ → CTA réutilisé. Les visuels signature sont injectés par la page.
 */
export default function ExpertisePage({
  content,
  breadcrumbLabel,
  heroVisual,
  scopeVisual,
  processVisual,
}: Props) {
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  return (
    <main className="relative pt-28 sm:pt-32 overflow-hidden isolate">
      {/* ── Background DA ── */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[1100px] -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-[600px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.22), rgba(59,93,255,0.06) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-32 top-[20%] w-[420px] h-[420px] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(59,93,255,0.18), transparent 70%)' }}
        />
      </div>

      {/* ── HERO ── */}
      <ExpertiseHero
        badge={content.badge}
        h1Lead={content.h1Lead}
        h1Highlight={content.h1Highlight}
        h1Tail={content.h1Tail}
        subhead={content.subhead}
        ctaPrimary={content.ctaPrimary}
        reassure={content.reassure}
        breadcrumbLabel={breadcrumbLabel}
        visual={heroVisual}
      />

      {/* ── HOOK différenciant ── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-24 sm:mt-32">
        <SectionHead eyebrow={content.hook.eyebrow} title={content.hook.title} />
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 text-lg sm:text-2xl text-ink-700 leading-snug max-w-3xl font-medium"
        >
          {content.hook.lead}
        </motion.p>
        <div className="mt-10 grid sm:grid-cols-3 gap-4 sm:gap-5">
          {content.hook.points.map((p, i) => (
            <motion.div
              key={p}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-ink-100 bg-ink-100 p-5 sm:p-6 hover:border-accent-200 hover:shadow-[0_20px_50px_-22px_rgba(59,93,255,0.4)] transition-all"
            >
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <p className="mt-4 text-sm sm:text-base text-ink-700 leading-relaxed">{p}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── PÉRIMÈTRE (scope) ── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-24 sm:mt-32">
        <SectionHead eyebrow={content.scope.eyebrow} title={content.scope.title} />
        {scopeVisual && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10"
          >
            {scopeVisual}
          </motion.div>
        )}
        <div className="mt-8 sm:mt-10">
          <ScopeTable
            yesTitle={content.scope.yesTitle}
            yes={content.scope.yes}
            noTitle={content.scope.noTitle}
            no={content.scope.no}
            note={content.scope.note}
          />
        </div>
      </section>

      {/* ── COMMENT ÇA MARCHE ── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-24 sm:mt-32">
        <SectionHead eyebrow={content.how.eyebrow} title={content.how.title} />
        {processVisual && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10"
          >
            {processVisual}
          </motion.div>
        )}
        <div className="relative mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6">
          {content.how.steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex flex-col items-start"
            >
              <div className="relative z-10 w-12 h-12 rounded-full bg-ink-100 border border-accent-200 flex items-center justify-center shadow-[0_10px_30px_-12px_rgba(59,93,255,0.4)]">
                <span className="font-mono text-sm font-semibold bg-gradient-to-br from-accent-600 to-accent-700 bg-clip-text text-transparent">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-5 text-lg font-semibold text-ink-950 tracking-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-ink-600 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CONFORMITÉ & GARANTIES ── */}
      <section className="relative max-w-6xl mx-auto px-5 sm:px-6 mt-24 sm:mt-32">
        <SectionHead eyebrow={content.compliance.eyebrow} title={content.compliance.title} />
        <div className="mt-10 grid sm:grid-cols-2 gap-4 sm:gap-5">
          {content.compliance.items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-2xl border border-ink-100 bg-ink-100 p-5 sm:p-6 hover:border-accent-200 hover:shadow-[0_20px_50px_-22px_rgba(59,93,255,0.4)] transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-accent-50 text-accent-700 border border-accent-100 flex items-center justify-center shrink-0">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-ink-950 tracking-tight">{it.title}</h3>
                  <p className="mt-1 text-sm text-ink-600 leading-relaxed">{it.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── FAQ (longue traîne SEO) ── */}
      <section className="relative max-w-4xl mx-auto px-5 sm:px-6 mt-24 sm:mt-32">
        <div className="text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-ink-500">{content.faqEyebrow}</span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            className="mt-3 font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(1.9rem,4.4vw,3rem)] leading-[1.1]"
          >
            {content.faqTitle}
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 sm:mt-12 rounded-2xl border border-ink-100 bg-ink-100 overflow-hidden"
        >
          {content.faq.map((f, i) => {
            const isOpen = openFaq === i
            return (
              <div key={f.q} className="border-b border-ink-100 last:border-0">
                <button
                  onClick={() => setOpenFaq(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-6 px-5 sm:px-6 py-5 text-left hover:bg-ink-50/50 transition-colors"
                >
                  <span className="font-medium text-ink-950">{f.q}</span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="shrink-0 w-7 h-7 rounded-full border border-ink-200 flex items-center justify-center text-ink-700"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                    </svg>
                  </motion.span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <p className="px-5 sm:px-6 pb-5 text-ink-600 leading-relaxed">{f.a}</p>
                </motion.div>
              </div>
            )
          })}
        </motion.div>
      </section>

      {/* ── CTA final (réutilisé) ── */}
      <CTA />
    </main>
  )
}
